export interface ModelService {
  id: string;
  title: string;
  protocol: string;
  /** Absent for a service that names no model, such as web search. */
  model?: string;
  vendor?: string;
  supports_vision?: boolean;
  supports_audio?: boolean;
  supports_video?: boolean;
  base_url?: string;
  /** The supply node that makes this service, when one does; such a service has no address and no key. */
  node?: string;
  enabled?: boolean;
  is_default?: boolean;
  verification?: {
    status: "passed" | "failed" | "unknown"; message: string; at: number; request_id: string; duration_ms?: number;
    /** What each ticked kind of input did in the test: a service passes only when every one did. */
    media?: Partial<Record<"image" | "audio" | "video", { status: "passed" | "failed" | "unknown"; message: string }>>;
  };
  key_present?: boolean;
  input_cny_per_million?: number;
  cached_input_cny_per_million?: number;
  output_cny_per_million?: number;
  call_price_cny?: number;
  context_tokens?: number;
  /** Why a saved service is offered to nobody until an administrator completes it; empty when usable. */
  problem?: string;
  max_output_tokens?: number;
  max_duration_seconds?: number;
  /** How long a chat service's supplier keeps a request cached, in seconds; absent when nobody said. */
  cache_seconds?: number;
}
/**
 * A machine that makes a service for the platform by connecting to the Hub itself. Times are unix seconds;
 * the last four fields come only while it is connected.
 */
export interface SupplyNode {
  id: string;
  title: string;
  /** Whether a connection code was ever redeemed and not since taken away. */
  paired: boolean;
  /** Whether an unredeemed, unexpired connection code is waiting. */
  code_pending: boolean;
  online: boolean;
  connected_at?: number;
  last_seen?: number;
  open_requests?: number;
  serves?: string[];
}
/** Where a node stands: 在线, 离线 (paired, not connected now), 待配对 (its code is waiting) or 未配对. */
export function nodeState(node: SupplyNode): string {
  if (node.online) return "在线";
  if (node.paired) return "离线";
  return node.code_pending ? "待配对" : "未配对";
}
export interface PlatformUsage {
  allowance_cny: number;
  charged_cny: number;
  reserved_cny: number;
  remaining_cny: number;
  calls: number;
  unknown_calls: number;
  /**
   * What was used, said by the Hub's one spend rule (`pa/lib/vocab/spend.py`): the figure, and the line that
   * qualifies it. A zero beside calls nobody could price is not drawn as `¥0.00`.
   */
  spend?: UsedSpend;
}
export interface UsedSpend {
  figure: string;
  note: string;
}
/** A kind of service the owner takes from the platform or from their own keys. */
export interface ServiceKind {
  kind: string;
  title: string;
}
/** What a new Agent of this account is given, and what the account has left to spend. */
export interface PlatformCatalog {
  offered: ServiceKind[];
  free_chat_available?: boolean;
  usage: PlatformUsage;
}
/** How the platform charges for a service: per million tokens, or per successful call. */
export interface ServiceRate {
  pricing: string;
  input_cny_per_million?: number;
  cached_input_cny_per_million?: number;
  output_cny_per_million?: number;
  call_price_cny?: number;
}
/** One service the platform offers, as its owner reads it. */
export interface QuotedService extends ServiceRate {
  title: string;
}
/** Where one kind of service comes from for one Agent. */
export interface KindSource extends ServiceKind {
  source: "platform" | "own";
  /** What the platform offers of this kind; empty when it offers nothing. */
  services: QuotedService[];
  /** Preferred settings entry; source choice is shared by every consumer below. */
  where: string;
  /** Every installed settings namespace that consumes this kind, as declared by the plugins. */
  namespaces: string[];
  /** Configuration readiness only: it does not claim a successful supplier request. */
  platform_connection: "ready" | "needs_apply" | "unavailable" | "unknown" | null;
}
export interface ModelSourceView {
  /** False while the Agent cannot be asked: its stored choices are shown and cannot be changed. */
  online: boolean;
  kinds: KindSource[];
  usage: PlatformUsage;
}
/** A balance or a charge, to the cent; a positive amount under a cent is not shown as nothing. */
export const accountAmount = (value: number) =>
  value > 0 && value < 0.01
    ? "<¥0.01"
    : `¥${value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** A unit price at the precision it was entered with; the Hub keeps six decimals. */
export const unitPrice = (value: number) => `¥${value.toLocaleString("zh-CN", { maximumFractionDigits: 6 })}`;
export function ServicePrice({ rate }: { rate: ServiceRate }) {
  const prices = rate.pricing === "tokens"
    ? [rate.input_cny_per_million, rate.cached_input_cny_per_million, rate.output_cny_per_million]
    : [rate.call_price_cny];
  if (prices.some(price => typeof price !== "number" || !Number.isFinite(price) || price < 0)) {
    return <>平台单价未填写</>;
  }
  if (rate.pricing === "free") return <>{rate.call_price_cny === 0 ? "免费" : "平台单价无效"}</>;
  return <>{rate.pricing === "tokens"
    ? `${unitPrice(rate.input_cny_per_million ?? 0)} 输入 / ${unitPrice(rate.cached_input_cny_per_million ?? 0)} 缓存命中 / ${unitPrice(rate.output_cny_per_million ?? 0)} 输出（每百万 token）`
    : `${unitPrice(rate.call_price_cny ?? 0)} / 次成功服务`}</>;
}

const isUsage = (usage: unknown): usage is PlatformUsage => {
  if (!usage || typeof usage !== "object") return false;
  const data = usage as PlatformUsage;
  return [data.allowance_cny, data.charged_cny, data.reserved_cny, data.remaining_cny, data.calls, data.unknown_calls]
    .every(field => typeof field === "number" && Number.isFinite(field) && field >= 0)
    && (data.spend === undefined || (typeof data.spend?.figure === "string" && typeof data.spend.note === "string"));
};
const isKind = (row: unknown): row is ServiceKind =>
  Boolean(row) && typeof row === "object" && [(row as ServiceKind).kind, (row as ServiceKind).title]
    .every(field => typeof field === "string");

/** An incomplete successful response is unread data, never a zero balance. */
export function platformCatalog(value: unknown): PlatformCatalog | undefined {
  if (!value || typeof value !== "object") return undefined;
  const data = value as PlatformCatalog;
  return Array.isArray(data.offered) && data.offered.every(isKind) && isUsage(data.usage) ? data : undefined;
}

/** The same for one Agent's sources: a kind with an unread source is never drawn as a choice. */
export function modelSourceView(value: unknown): ModelSourceView | undefined {
  if (!value || typeof value !== "object") return undefined;
  const data = value as ModelSourceView;
  const complete = typeof data.online === "boolean" && isUsage(data.usage) && Array.isArray(data.kinds)
    && data.kinds.every(row => isKind(row) && (row.source === "platform" || row.source === "own")
      && typeof row.where === "string" && Array.isArray(row.services)
      && Array.isArray(row.namespaces) && row.namespaces.every(namespace => typeof namespace === "string")
      && row.namespaces.includes(row.where)
      && (row.source === "own" ? row.platform_connection === null
        : typeof row.platform_connection === "string" && ["ready", "needs_apply", "unavailable", "unknown"].includes(row.platform_connection))
      && row.services.every(service => service && typeof service.title === "string" && typeof service.pricing === "string"));
  return complete ? data : undefined;
}
